const EXCEL_FILE_PATTERN = /\.(xlsx|xls)$/i;
const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

let storage = null;
let connectedEmail = '';
let MegaStorage = null;

const loadMegaStorage = async () => {
  if (!MegaStorage) {
    const mega = await import('megajs');
    MegaStorage = mega.Storage;
  }

  return MegaStorage;
};

const mapMegaFile = (file) => ({
  id: file.nodeId || file.downloadId || file.name,
  name: file.name || 'MEGA file',
  size: file.size || 0,
  modifiedTime: file.timestamp ? new Date(file.timestamp * 1000).toISOString() : null,
  rawFile: file
});

const normalizePath = (path) => String(path || '')
  .split('/')
  .map(part => part.trim())
  .filter(Boolean);

const getConfiguredRoot = (folderPath) => {
  if (!folderPath) return storage.root;
  const root = storage.root.navigate(normalizePath(folderPath));
  if (!root || !root.directory) {
    throw new Error(`Cartella MEGA non trovata: ${folderPath}`);
  }
  return root;
};

const ensureConnected = () => {
  if (!storage) {
    throw new Error('Accedi a MEGA prima di importare questo file.');
  }
};

export const megaProvider = {
  id: 'mega',
  name: 'MEGA',
  fileListTitle: 'File Excel su MEGA',
  emptyState: 'Accedi a MEGA per vedere i file Excel disponibili.',
  connectLabel: 'Importa da MEGA',
  loadingLabel: 'Collegamento a MEGA...',
  scanningLabel: 'Scansione MEGA in corso...',
  requiresCredentials: true,
  credentialFields: [
    { id: 'email', label: 'Email MEGA', type: 'email', autoComplete: 'username' },
    { id: 'password', label: 'Password MEGA', type: 'password', autoComplete: 'current-password' },
    { id: 'secondFactorCode', label: 'Codice 2FA (opzionale)', type: 'text', autoComplete: 'one-time-code' }
  ],
  credentialsNote: 'Le credenziali MEGA restano solo in memoria per questa sessione e non vengono salvate.',
  isConfigured: () => true,

  connect: async (credentials = {}) => {
    if (storage && (!credentials.email || connectedEmail === credentials.email)) {
      return storage;
    }

    if (!credentials.email || !credentials.password) {
      throw new Error('Inserisci email e password MEGA.');
    }

    if (storage) {
      await storage.close().catch(() => {});
      storage = null;
    }

    const Storage = await loadMegaStorage();
    storage = await new Storage({
      email: credentials.email,
      password: credentials.password,
      secondFactorCode: credentials.secondFactorCode || undefined,
      keepalive: false
    }).ready;
    connectedEmail = credentials.email;
    return storage;
  },

  refresh: async () => {
    ensureConnected();
    await storage.reload(true);
    return storage;
  },

  listFiles: async (config = {}) => {
    ensureConnected();
    const root = getConfiguredRoot(config.folderPath);
    return root
      .filter(file => !file.directory && EXCEL_FILE_PATTERN.test(file.name || ''), true)
      .map(mapMegaFile)
      .sort((a, b) => new Date(b.modifiedTime || 0) - new Date(a.modifiedTime || 0))
      .slice(0, 20);
  },

  listFolders: async (config = {}, options = {}) => {
    ensureConnected();
    if (options.forceRefresh) {
      await megaProvider.refresh();
    }

    const basePath = normalizePath(config.folderPath).join('/');
    const root = getConfiguredRoot(basePath);

    return root
      .filter(file => file.directory, false)
      .map(folder => ({
        id: folder.nodeId || `${basePath}/${folder.name}`,
        name: folder.name || 'Cartella MEGA',
        path: [basePath, folder.name].filter(Boolean).join('/')
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  downloadFile: async (file) => {
    ensureConnected();
    const megaFile = file.rawFile || storage.files[file.fileId || file.id];

    if (!megaFile) {
      throw new Error('File MEGA non trovato. Ricarica la lista e riprova.');
    }

    const data = await megaFile.downloadBuffer({});
    return new File([data], file.name || megaFile.name, {
      type: EXCEL_MIME_TYPE
    });
  },

  getRecentEntry: (file) => ({
    provider: 'mega',
    providerName: 'MEGA',
    name: file.name,
    size: file.size,
    fileId: file.id,
    modifiedTime: file.modifiedTime
  }),

  getFileFromRecent: (entry) => ({
    id: entry.fileId,
    fileId: entry.fileId,
    name: entry.name,
    size: entry.size,
    modifiedTime: entry.modifiedTime
  })
};
